import { useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import Tooltip from '@mui/material/Tooltip'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Alert from '@mui/material/Alert'
import Link from '@mui/material/Link'
import { LuChevronRight, LuInfo, LuZap } from 'react-icons/lu'

import useOrganizations from '../models/useOrganizations'
import useBilling, { isFreePlan, isPaidPlan } from '../models/useBilling'
import useUsage, {
  type UsageAxis,
  formatPeriod,
  orgTrafficFromAxes,
  currentPeriod,
  periodBounds,
  projectedBillCents,
} from '../models/useUsage'
import { previousPeriod, recentPeriods } from '../utilities/usagePeriods'
import { flatBaseCents } from '../utilities/subscription'
import useTasks from '../models/useTasks'
import UsageBoost from './UsageBoost'
import { freshnessLabel, resetLabel, resetsOn } from '../utilities/freshness'
import { useOrgRole } from '../hooks/useOrgRole'
import CreditBlock from './CreditBlock'
import ComputeUsage from './ComputeUsage'
import ProvisionedTasks from './ProvisionedTasks'
import { UNREADABLE_CREDIT_REASON, throttleConsequence, throttleLimitCause, type BillingLimitCause } from '../utilities/computeStop'
import type { CreditPosition } from '../models/useBilling'
import { reconcileStorage, type StorageReconciliation } from '../utilities/storage'
import { capitalize, formatBytes, formatCents } from '../utilities/general'
import { bareInput } from '../utilities/formAlign'
import {
  allowanceFraction,
  AXIS_LABEL,
  AXIS_PALETTE,
  formatAxisAmount,
  reconcileNodes,
  type NodeReconciliation,
} from '../utilities/usageAxis'
import { useModalClose } from '../hooks/useModalClose'
import Modal from './Modal'
import { DOCS_BILLING_URL } from '../constants'
import UsageOverTime from './UsageOverTime'
import UsageRings from './UsageRings'
import TrafficMatrix from './TrafficMatrix'

// Every axis `get_org_usage` gives an allowance to. ⚠ ALL FOUR, and compute is the one that was
// missing (app#152): the app-wide banner has always been able to name a "compute allowance", and
// this panel — the screen that banner links to — could not show compute at all, on the axis whose
// Free consequence is the harshest (running tasks suspended, not just refused).
// The order the axes are drawn, rings and bars alike — the two METERED axes outermost/first, then
// the two whose allowance is real but whose overage is a structural zero (router migration 0108).
const AXIS_ORDER = ['bandwidth', 'compute', 'nodes', 'storage'] as const

// Why is the org throttled? Derived from the same signals the banner uses (lapse / credit / free
// bandwidth). A throttled org is always paid-lapsed, paid-over-allowance, or free-over-allowance;
// the neutral fallback only shows in the brief window before the plan has loaded.
// ⚠ The CAUSE and its prose are derived together, from one branch each (app#163). The alert
// needs both — the reason to print, and whether that reason also stops provisioned compute — and
// deriving them in two places is how the sentence comes to name a cause the consequence does not
// match. `BillingBanner` spells the causes the same way, and both read `throttleConsequence`.
//
// ⚠⚠ THE CREDIT ARM IS HERE FOR THE SAME REASON IT IS IN THE BANNER, AND MUST STAY IN STEP WITH IT
// (app#145). Under credit billing the spend cap throttles nothing — it bounds PURCHASES — so
// "monthly spend cap reached" would name an object that did not cause the throttle, on the one
// panel a customer opens to find out why. `throttleLimitCause` puts it ahead of the cap arm and
// replaces it, exactly as the banner orders them; a Member, an older router and an arrears
// deployment all leave `credit` null and fall through to `cap`, which names the allowance and no
// longer the cap (app#288) — the same wording the banner falls to, from the same constant.
function throttleCause(
  status: string,
  isPaid: boolean,
  isFree: boolean,
  credit: CreditPosition | null,
): { cause: BillingLimitCause, reason: string } {
  // ⚠ The CAUSE comes from `throttleLimitCause`, which the node page's allowance line reads too
  // (app#323). Only the reason is this panel's own.
  const cause = throttleLimitCause(status, isPaid, isFree, credit)
  switch (cause) {
    case 'lapsed':
      return { cause, reason: 'subscription lapsed' }
    // ⚠ THREE CREDIT CAUSES, ONE PER STATE (app#269). `credit_landed` is the window after a
    // purchase in which the fail-open stamp has not yet lapsed: the balance is positive, so "empty"
    // would be false and "add credit" would ask for the thing just done. The sentence says the
    // limit is lifting, and `creditThrottleCause` is the one place the three are told apart.
    case 'spending_off':
      return { cause, reason: 'the allowance is used up and paying from your balance is switched off' }
    case 'credit_landed':
      return {
        cause,
        reason:
          'the allowance is used up. Your balance is positive, or a purchase is settling, so full speed resumes within a few minutes',
      }
    case 'credit_empty':
      return { cause, reason: 'the allowance is used up and your credit balance is empty' }
    // ⚠⚠ THE PAID FALLBACK NAMES NO CAP (app#288). It is reached when the credit position is
    // unreadable — a Member, an older router, an arrears deployment — and it said "monthly spend
    // cap reached". Under credit the cap bounds automatic refills only (router#1195, #1264, #1279),
    // so it slowed nothing. `UNREADABLE_CREDIT_REASON` is the same sentence `BillingBanner`
    // renders: two homes for one sentence is what let the arrears wording survive here, and
    // app#163 is the rule that the two surfaces state one cause one way.
    case 'cap':
      return { cause, reason: UNREADABLE_CREDIT_REASON }
    case 'allowance':
      return { cause, reason: 'free bandwidth allowance exceeded' }
    case 'limit':
      return { cause, reason: 'a billing limit was reached' }
  }
}

// ⚠ Both read the axis's OWN unit. Compute is denominated in cents, so a byte formatter would
// print "1.2 KB" for twelve dollars — which is why the dispatch is on `unit` and not on the name.
// ⚠ Compute reads MONEY on this panel (app#327): no axis converts its unit any more.
const axisValue = (axis: UsageAxis): string => formatAxisAmount(axis.unit, axis.used)

const axisIncluded = (axis: UsageAxis): string =>
  axis.included == null ? 'Unlimited' : formatAxisAmount(axis.unit, axis.included)

type Props = { inline?: boolean }

export default function Usage({ inline }: Readonly<Props>) {
  const theme = useTheme()
  const { open, handleClose } = useModalClose('/', inline)
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  const {
    period,
    loading,
    axes,
    networks,
    nodeStorage,
    compute,
    computeError,
    usageSeries,
    previousSeries,
    grants,
    freshness,
    services,
    planName,
    baseCents,
    get,
    loadServices,
    setPeriod,
  } = useUsage()
  const {
    status, plan, plans, throttledUntil, blockedUntil, credit, loadCredit, loadSummary, clearSummary, summaryLoaded, interval, consequences,
  } = useBilling()
  const { can } = useOrgRole()
  const loadTasks = useTasks(t => t.get)
  const canManageBilling = can('billing.manage') // Owner only — the router re-checks
  const [expanded, setExpanded] = useState<string>()

  // The bill figure, at render (app#264). A past period has nothing left to project and its flat
  // fee is not known here, so it prints the usage drawn in that month, under a title that says so;
  // an annual subscription owes no monthly fee, so the current month projects overage alone.
  // `flatBaseCents` carries both rules; a null interval keeps the monthly fee.
  const pastPeriod = period < currentPeriod()
  // The month whose last days the over-time chart draws in grey before this one. ⚠ Null for a past
  // period, which holds all its days and is drawn alone, and below the usage epoch.
  const priorPeriod = pastPeriod ? null : previousPeriod(period)
  const bill = projectedBillCents(axes, period, flatBaseCents(baseCents, interval, period, currentPeriod()))

  // Fetch on open and whenever the active org changes (an org switch re-scopes everything).
  useEffect(() => {
    void get()
  }, [activeOrgId])

  // The provisioned machines and their deadlines (router#968). Read from `tasks` over PostgREST,
  // not from the usage RPCs: a deadline is a fact about one container, and the compute breakdown
  // above aggregates by machine class. It is a member-level read — the router keeps the node
  // lifecycle at Member — so unlike `loadCredit` below it needs no role check.
  useEffect(() => {
    void loadTasks()
  }, [activeOrgId])

  // The credit position (app#170 items 3-4). ⚠ Owner-only on the router, so a Member must not
  // call it — the read would be refused and the error would be the only thing that changed on
  // their screen. Same rule Billing.tsx applies to `loadSummary`.
  //
  // ⚠ `loadCredit` swallows its own failures into `credit: null`, and `CreditBlock` renders
  // nothing without a position — so an arrears deployment, an older router and a refused member
  // all reach the same silent, correct outcome.
  useEffect(() => {
    if (canManageBilling) void loadCredit()
  }, [activeOrgId, canManageBilling])

  // The billing summary, for the payment method `CreditBlock` names (app#268). The block said
  // "Charged to Link, now." on /billing and "Charged to your default payment method, now." on
  // this page, about the same organization, because only /billing loaded the card. Same
  // Owner gate as `loadSummary` on `Billing.tsx`, for the same reason.
  //
  // ⚠⚠ CLEARED ON EVERY ORG SWITCH, BEFORE ANYTHING IS FETCHED — the rule `Billing.tsx` applies,
  // and it matters more here: a Member of the new org never runs the read, so without the clear
  // the previous org's card would name the instrument this org's purchase lands on.
  useEffect(() => {
    clearSummary()
  }, [activeOrgId])

  // Guarded on `summaryLoaded` here only: this page reads the card, and a read that landed is
  // current until the org switch above clears it. `Billing.tsx` owns the invoice list and refetches.
  useEffect(() => {
    if (canManageBilling && !summaryLoaded) void loadSummary()
  }, [activeOrgId, canManageBilling, summaryLoaded, loadSummary])

  const axisFor = (name: string): UsageAxis | undefined => axes.find(a => a.axis === name)
  // ⚠⚠ COUNTED, NOT BILLED — devices bill on NO plan (Benoit, 2026-09-05). `0108` dropped
  // `node_overage_cents` and `get_billable_usage_admin`'s overage sums bandwidth and compute only;
  // `0119` set Free's `node_limit` to 0, so no offered plan even caps them. See `usageAxis.ts`.
  const countedNodes = Math.round(axisFor('nodes')?.used ?? 0)
  // Keyed on the plan's hard_caps flag, not on `planName` — same derivation the banner and the SSO
  // upsell use, so a renamed or added hard-capped plan can't read free here and paid there (app#127).
  const isFree = isFreePlan(plan)

  // Throttle status (2.11) — org-level decision (organizations.throttled_until); when set, EVERY
  // node of the org is capped to 1 Mbps on the billed legs. Surfaced here, at node level, and
  // via the app-wide banner, so reduced bandwidth reads as a billing limit, not a product fault.
  const isPaid = isPaidPlan(plan)
  const throttled = !!throttledUntil && new Date(throttledUntil).getTime() > Date.now()
  const throttle = throttled ? throttleCause(status, isPaid, isFree, credit) : null
  // Blocked (organizations.blocked_until, router#966) — a Free org past its bandwidth allowance
  // has its customer data REFUSED, not shaped. The banner is dismissed per mount; this panel is
  // where the state stays readable, which matters more for a block than for a throttle.
  // ⚠ It suppresses the throttle line below rather than sitting beside it — two bandwidth
  // verdicts in one panel is how a reader decides neither is real.
  const blocked = !!blockedUntil && new Date(blockedUntil).getTime() > Date.now()

  // Ring colors from the theme, outermost first — most-cost-driving out, so the two axes that can
  // actually bill (bandwidth, compute) are the two outer rings.
  const ringColor: Record<string, string> = Object.fromEntries(
    Object.entries(AXIS_PALETTE).map(([axis, slot]) => [axis, theme.palette[slot].main])
  )

  // The storage axis has two halves and the drill-down must show both, or its rows cannot be added
  // up to the ring above them (app#135). Networks carry network storage; nodes carry theirs, which
  // belongs to no network at all. Reconcile the two against the org's own axis and SAY the result —
  // an unexplained difference on a billing screen is worse than a larger number.
  const storage = reconcileStorage(networks, nodeStorage, axisFor('storage')?.used ?? 0)
  // The same treatment for the nodes axis (app#209 item 1). Three counts sat on this page — the
  // header's node count, the Nodes ring, and a network row's "20 nodes" — and nothing said
  // that the first two are the COUNTED total and the third is the ACTIVE one. The rule storage
  // follows applies here too: rows under a total must add up to it, and where they do not, name
  // the difference rather than leave the reader to guess which number is wrong.
  const nodes = reconcileNodes(networks, countedNodes)
  const nodeStorageRows = [...nodeStorage]
    .filter(n => n.storageBytesPeak > 0)
    .sort((a, b) => b.storageBytesPeak - a.storageBytesPeak)

  const toggleNetwork = (networkId: string) => {
    if (expanded === networkId) {
      setExpanded(undefined)
    } else {
      setExpanded(networkId)
      void loadServices(networkId)
    }
  }

  return (
    <Modal open={open} inline={inline} maxWidth="md" onClose={inline ? undefined : handleClose}>
      <DialogContent>
        {/* ⚠ THE ROW WRAPS BELOW `sm`, the same reason and the same shape as the interval row in
            `Billing.tsx`. The panel is a dialog and at phone width the paper is about 310 px,
            where the period picker alone takes 150: the title and "Pro · 15 nodes" were
            squeezed into a three-line column of single words beside it. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            mb: 2
          }}>
          <Stack direction="row" spacing={1.5} sx={{
            alignItems: 'baseline'
          }}>
            {/* ⚠ `component="h2"` BECAUSE `CreditBlock` IS SHARED (app#212). That block titles
                itself `h3` for the billing panel, and this panel renders the same component — so
                without a real top-level heading here its section would sit above the page it is
                on. The rest of this panel's hierarchy is a separate pass; app#212 covers the
                billing panel. */}
            <Typography variant="subtitle1" component="h2" color="secondary">
              Usage
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary'
            }}>
              {capitalize(planName ?? 'Free')} ·{' '}
              {countedNodes} {countedNodes === 1 ? 'node' : 'nodes'}
            </Typography>
          </Stack>
          <Select size="small" value={period} onChange={e => setPeriod(e.target.value)} sx={{ ...bareInput, minWidth: 150 }}>
            {recentPeriods(6).map(p => (
              <MenuItem key={p} value={p}>
                {formatPeriod(p)}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        {/* ⚠⚠ SAY WHAT THE PERIOD IS (app#146 item 1). A picker labelled "September 2026" does not
            tell a customer that the period is the CALENDAR MONTH — which is the question someone
            reading an invoice actually has.

            ⚠⚠ IT DOES NOT SAY "BILLED ON THE 1ST", THOUGH app#146 ASKS FOR THAT, BECAUSE IT IS
            ONLY TRUE OF MONTHLY. `CreateCheckoutSession` sets `BillingCycleAnchor` inside
            `if !annual` (router#1058), so a MONTHLY subscription invoices on the 1st and an ANNUAL
            one on its checkout anniversary. router#1004 anchored the monthly cycle and closed;
            it did not make one invoice date true for everyone. A sentence true of one interval
            and false of the other is the app#163 defect in a different regime.

            ⚠ What IS true for everyone is the ALLOWANCE reset, which is what this page is about:
            every allowance is deposited and expires on the calendar month, for Free and paid,
            monthly and annual alike. The invoice date belongs on the billing page, which reads
            `current_period_end` from Stripe and therefore knows the real one.

            ⚠ UTC, and stated: `currentPeriod()` slices an ISO string and the server's
            `PeriodBounds` agrees, so the boundary is midnight UTC on the 1st. A reader west of
            Greenwich who assumes local time is a day out on a billing screen.

            ⚠⚠ THE ONE DOCUMENTATION LINK ON THIS PANEL (app#211, the Usage half). This page made
            about a dozen pricing claims — the decimal gigabyte, the compute class rates, what
            stops at each cap — and linked NOTHING, while
            `docs.nobgp.com/plans-and-billing` answers every one of them. It rides on this caption
            rather than arriving as a new line, so the panel explains more and says less. Hygiene
            matches the billing panel's link and the invoice link in `BillingAccount.tsx`:
            `target="_blank"` with `rel="noreferrer"`.

            ⚠ ONE LINK, NOT TWO, AND THE COMPUTE ANCHOR IS DELIBERATELY LEFT OUT. app#211 offers a
            deeper `#provisioned-compute` link from the compute lines; the anchor does exist
            (measured 2026-09-03). The block that would carry it, `ComputeUsage`, rendered only
            when compute was non-zero until 2026-09-14, so the deeper link was left out and the
            general link is what a reader gets either way. The
            compute figures read in dollars, so no footnote is needed to explain a unit. */}
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
          Usage periods are calendar months. Every allowance resets at midnight UTC on the 1st.{' '}
          <Link href={DOCS_BILLING_URL} target="_blank" rel="noreferrer" variant="caption">
            How billing works
          </Link>
        </Typography>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Blocked — org-wide, customer data REFUSED (router#966). Outranks the throttle line. */}
        {blocked && (
          <Alert severity="error" icon={<LuZap size={18} />} sx={{ mb: 2 }}>
            Data transfer is <strong>stopped</strong> — free bandwidth allowance used up. Your devices are still online
            and your files are still there; only transfers are paused. This clears at the start of your next billing
            period, or upgrade to resume within a few minutes.
          </Alert>
        )}

        {/* Throttle status — org-wide, every node capped to 1 Mbps on the billed legs (2.11). */}
        {!blocked && throttle && (
          <Alert severity="warning" icon={<LuZap size={18} />} sx={{ mb: 2 }}>
            Bandwidth throttled to <strong>1 Mbps per node</strong> — {throttle.reason}.{' '}
            {/* The same served booleans the banner reads (app#270); null keeps today's sentence. */}
            {throttleConsequence(throttle.cause, consequences)}
          </Alert>
        )}

        {/* An allowance larger than the plan sold, and why (app#171). Above the meters, because it
            is the explanation for the numbers in them. Renders nothing when nothing is granted. */}
        <UsageBoost grants={grants} period={period} currentPeriod={currentPeriod()} />

        {/* Headline rings + projected spend */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          sx={{
            alignItems: 'center',
            mb: 3
          }}>
          <UsageRingsBlock ringColor={ringColor} axisFor={axisFor} billCents={bill} label={pastPeriod ? 'usage cost' : 'projected'} />
          <Stack
            spacing={2}
            sx={{
              flex: 1,
              width: '100%'
            }}>
            {AXIS_ORDER.map(name => {
              const axis = axisFor(name)
              return axis ? (
                <UsageBar
                  key={name}
                  axis={axis}
                  isFree={isFree}
                  isPaid={isPaid}
                  color={ringColor[name]}
                  resets={resetLabel(period, currentPeriod(), periodBounds(period).end)}
                  freshness={freshnessLabel(freshness[axis.axis])}
                />
              ) : null
            })}
          </Stack>
        </Stack>

        {/* Usage over time (app#146 item 3, router#1165's app half). Directly under the rings and
            bars: those say HOW MUCH of the period's allowance is gone, this says WHEN it went,
            which is the first question after a surprising number. Renders nothing for a period in
            which no axis moved. */}
        {/* ⚠⚠ `billed` IS WHAT LETS THE CHART CLAIM ANYTHING. It is `get_org_usage`'s figure per
            axis — the same number the bars above draw — so the chart can say "the days add up"
            only where they do, and NAME the difference where they do not. The two are read from
            different records and an aged period keeps only one of them (`seriesReconciles`). */}
        {/* ⚠ `period` is the chart's width: as many slots as the period has days. `included` is the
            dotted line: one day's share of the allowance. `previous` is the month before, whose
            last days are in grey before the period. The scale follows the days drawn. */}
        <UsageOverTime
          rows={usageSeries}
          previous={priorPeriod ? { rows: previousSeries, label: formatPeriod(priorPeriod) } : undefined}
          period={period}
          billed={{ bandwidth: axisFor('bandwidth')?.used, compute: axisFor('compute')?.used }}
          included={{ bandwidth: axisFor('bandwidth')?.included, compute: axisFor('compute')?.included }}
        />

        {/* The compute breakdown (app#136 item 3). Above the credit block: it is usage, and the
            credit block is what PAYS for usage. In a month with no compute it still shows the
            budget (`showComputeCard`), and renders nothing only when there is no pool to show.
            ⚠ `usedCents`, `includedCents` and `overageCents` are the compute axis's own figures, the
            ones the bar above prints, so the card and the bar state one amount for each. */}
        <ComputeUsage
          rows={compute}
          error={computeError}
          period={period}
          usedCents={axisFor('compute')?.used}
          includedCents={axisFor('compute')?.included}
          overageCents={axisFor('compute')?.overageCents}
          isFree={isFree}
          isPaid={isPaid}
          planName={planName}
          plan={plan}
          plans={plans}
          credit={credit}
          consequences={consequences}
        />

        {/* The machines behind those hours, and how close each is to the deadline that stops it
            (router#968). Under the breakdown on purpose: the breakdown says what compute COST,
            this says what is still running and what is about to stop. Renders nothing for an org
            that has never provisioned. */}
        <ProvisionedTasks />

        {/* The usage-credit block (app#170). Directly under the meters on purpose: "what happens
            when this bar fills" is a question asked while looking at the bar, which is why the
            block is repeated here rather than living only on the billing page. Renders nothing
            for Free, for the exempt tier, or on a deployment that invoices in arrears. */}
        <CreditBlock isFree={isFree} />

        {/* Directional traffic — org total, aggregated by owner (get_org_usage's directional axes).
            receive = into the node (billed except relay/storage transmit); transmit = out of the node.

            ⚠ A CARD, LIKE EVERY OTHER SECTION OF THIS PANEL. `UsageOverTime` and `ComputeUsage`
            draw themselves in an outlined `Paper` and this block did not, so the panel read as
            two finished sections followed by three loose ones. The heading is `h3` for the same
            reason `Billing.tsx`'s are — the panel title above is the only `h2` (app#212). */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" component="h3">
            Traffic
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Every byte your devices moved this period. The bottom-right total is what the Bandwidth
            meter counts.
          </Typography>
          <TrafficMatrix traffic={orgTrafficFromAxes(axes)} />
        </Paper>

        {/* Projected bill — no denominator, because no cap bounds it */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'baseline'
            }}>
            <Typography variant="subtitle2" component="h3">
              {pastPeriod ? 'Usage cost' : 'Projected bill'} — {formatPeriod(period)}
            </Typography>
            <Typography variant="h6" component="p">{formatCents(bill)}</Typography>
          </Stack>
          {/* ⚠ THREE CAPTIONS FOR THREE FIGURES (app#264). A past month is over, so nothing is
              projected and the fee it owed is on its invoice, not in this panel. An annual
              subscription paid its year at checkout, so the month carries overage alone. The
              monthly form is what a null interval reads too. */}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {pastPeriod
              ? 'The overage drawn in that month. Plan fees are on the invoices in the billing portal, which are the authoritative ones.'
              : interval === 'year'
                ? 'This period’s overage so far, projected to the end of the month. Your annual plan fee is not a monthly charge. The invoice in the billing portal is the authoritative one.'
                : 'Your plan’s flat price plus this period’s overage so far, projected to the end of the month. The invoice in the billing portal is the authoritative one.'}
          </Typography>
          {/* ⚠⚠ THERE IS NO CAP GAUGE HERE ANY MORE, AND ONE MUST NOT COME BACK (app#202). A
              gauge labelled "Monthly spend cap" stood here, filled `projectedBillCents /
              spendCapCents`. nobgp-router#1195 narrowed that column to bound AUTOMATIC CREDIT
              REFILLS, so the fraction put the flat plan fee — which no cap bounds — over a ceiling
              on credit purchases. A Business org ($200 base, $100 refill cap) rendered RED at 200%
              on day one having spent nothing.

              ⚠⚠ IT WAS DELETED RATHER THAN REPAIRED, AND THE DUPLICATION IS WHY. `CreditBlock`
              renders on THIS page, below, and already draws the same column correctly — "Monthly
              cap" with "Refilled this month" progress, numerator `autoRefilledThisPeriodCents`.
              A second gauge over one column is the defect; repairing this one would give the page
              two gauges of one budget, whose agreement would then have to be maintained. And they
              would not even appear together: `CreditBlock` renders only for an Owner, only on a
              credit-billing deployment, and this gauge was gated on neither.

              ⚠⚠ APP#158's RULE IS KEPT, BY THE READER THAT HAS THE RIGHT NUMERATOR. That issue
              established that a Free org holding a STALE cap — set before app#157 removed the
              control, or left by a downgrade — must not be shown a cap as if it applied, because
              a hard-caps plan bills no overage. Benoit, 2026-08-27: "no spend cap on free, it does
              not make sense since there is no overage or credit or auto-refill etc…". `showsCredit`
              takes `isFree` and returns false, so `CreditBlock` shows a Free org nothing; the
              gauge's deletion satisfies the same rule the other way, for every plan.

              ⚠ `isFreePlan` stays the ONE predicate. `isFree` above is still what the bars read,
              and it is what this panel hands `CreditBlock` — the same derivation the banner,
              Billing and the SSO upsell use, so a renamed or newly-added hard-capped plan cannot
              read free here and paid there (app#127). `isFreePlan(null)` is deliberately false, so
              an unknown plan is treated as one that can bill; that direction is unchanged.

              ⚠ Nothing replaces the gauge with a sentence about what DOES bound the bill. Under
              credit billing the invoice is the base plus the credit actually purchased, which is
              not the estimate above it, and a panel that guessed at the relationship would make a
              statement about a customer's money that the app cannot support. The caption above
              says what the number is and where the authoritative one lives, which is true for
              every plan and every deployment. */}
        </Paper>

        {/* Where the usage came from — by network (→ services) and by node.

            ⚠ THE HEADING SAYS WHAT THE SECTION ANSWERS. "Cost hotspots" named a diagnosis rather
            than a question, on rows that are not costs at all — they are bytes and peaks, and on
            Free they can never be a cost. The two sub-lists keep their own captions. */}
        <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
          <Typography variant="subtitle2" component="h3">
            Where it went
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Networks first, then the storage that belongs to a device rather than to a network.
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            By network
          </Typography>
          <Stack spacing={0.5} sx={{
            mb: 2
          }}>
            {networks.length === 0 && (
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>
                No network usage this period.
              </Typography>
            )}
            {[...networks]
              .sort((a, b) => b.bandwidthBytes - a.bandwidthBytes)
              .map(net => (
                <Box key={net.networkId}>
                  <Stack
                    direction="row"
                    spacing={1}
                    onClick={() => toggleNetwork(net.networkId)}
                    sx={{
                      alignItems: 'center',
                      cursor: 'pointer',
                      py: 0.5
                    }}>
                    <IconButton size="small" sx={{ transform: expanded === net.networkId ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }}>
                      <LuChevronRight size={16} />
                    </IconButton>
                    <Typography variant="body2" sx={{
                      flex: 1
                    }}>
                      {net.networkName}
                      {/* Deleted mid-period. It is on the invoice — get_org_usage bills the peak it
                          reached while it was live (router 0089) — so hiding it here would shrink the
                          rows below the ring again, the other half of app#135. */}
                      {net.networkDeleted && (
                        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                          (deleted)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{
                      color: 'text.secondary'
                    }}>
                      {/* ⚠ "bandwidth", NOT "billed" — the same rule that took "billed" off the
                          nodes (2026-09-05). On a `hard_caps` plan bandwidth past the allowance
                          STOPS and is never a bill (`subscription.ts`), and this row does not read
                          the plan, so it may not name a charge. */}
                      {formatBytes(net.bandwidthBytes)} bandwidth · {formatBytes(net.storageBytesPeak)} network storage ·{' '}
                      {net.nodeCountPeak} nodes
                    </Typography>
                  </Stack>
                  <Collapse in={expanded === net.networkId} unmountOnExit>
                    <Stack spacing={0.5} sx={{ pl: 5, pb: 1 }}>
                      {(services[net.networkId] ?? []).length === 0 ? (
                        <Typography variant="caption" sx={{
                          color: 'text.secondary'
                        }}>
                          No service traffic.
                        </Typography>
                      ) : (
                        services[net.networkId].map(svc => (
                          <Stack key={svc.serviceId} direction="row" sx={{
                            justifyContent: 'space-between'
                          }}>
                            <Typography variant="caption">{svc.serviceTitle || 'service'}</Typography>
                            <Typography variant="caption" sx={{
                              color: 'text.secondary'
                            }}>
                              <Tooltip
                                arrow
                                title="Requests in from clients are always free. Metered bandwidth is the bytes forwarded to your node plus responses out to the internet."
                              >
                                <span>
                                  {formatBytes(svc.receiveBytes + svc.transmitBytes)} metered · to node{' '}
                                  {formatBytes(svc.receiveBytes)} · to internet {formatBytes(svc.transmitBytes)}
                                </span>
                              </Tooltip>
                            </Typography>
                          </Stack>
                        ))
                      )}
                    </Stack>
                  </Collapse>
                </Box>
              ))}
          </Stack>

          <NodeReconcileNote nodes={nodes} />

          {/* Node storage — the second half of the storage axis, and until app#135 it was on no
              screen at all. A node's storage follows the MACHINE (router 0086/0087): it belongs to
              no network, so it can never appear in a per-network row, and a drill-down that omits it
              simply does not add up to the ring. Deleted nodes are listed for the same reason
              deleted networks are — the peak is billed. */}
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            Node storage
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {nodeStorageRows.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', pl: 5 }}>
                No node storage this period.
              </Typography>
            )}
            {nodeStorageRows.map(node => (
              <Stack
                key={node.nodeId}
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  pl: 5,
                  py: 0.5
                }}>
                <Typography variant="body2" sx={{
                  flex: 1
                }}>
                  {node.nodeName || 'node'}
                  {node.nodeDeleted && (
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                      (deleted)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" sx={{
                  color: 'text.secondary'
                }}>
                  {formatBytes(node.storageBytesPeak)} storage
                </Typography>
              </Stack>
            ))}
          </Stack>

          <StorageReconcileNote storage={storage} />
        </Paper>
      </DialogContent>
    </Modal>
  )
}

// The one line that lets a reader ADD UP the rows above. Two storage halves are listed on this
// screen — per-network and per-node — and the ring at the top is their sum. State the sum, and when
// it does not match, name the gap instead of leaving the reader to find it (app#135 / DS-16).
function StorageReconcileNote({ storage }: Readonly<{ storage: StorageReconciliation }>) {
  const sum = `Storage: networks ${formatBytes(storage.networkBytes)} + nodes ${formatBytes(storage.nodeBytes)} = ${formatBytes(storage.accountedBytes)}`
  let verdict: string
  if (storage.reconciles) {
    verdict = ' — the Storage ring above.'
  } else if (storage.residualBytes > 0) {
    // The ring counts a subject no row names. The reachable cause is a network or node deleted
    // this period whose row the drill-down could not attribute — its peak is still on the invoice.
    verdict = `, and the Storage ring counts ${formatBytes(storage.residualBytes)} more. A network or node deleted this period keeps the peak it reached on the invoice.`
  } else {
    // The rows exceed the ring. The rows and the ring are separate reads taken moments apart.
    verdict = `, more than the Storage ring's ${formatBytes(storage.ringBytes)}. These are separate readings taken moments apart.`
  }
  return (
    <Tooltip
      arrow
      title="Storage is counted per org at the period's peak — a plan limit, never a charge. Network storage belongs to a network; a node's storage follows the machine and belongs to no network, so it is listed separately. The ring is the sum of both."
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {sum}
        {verdict}
      </Typography>
    </Tooltip>
  )
}

// The line that lets a reader reconcile the THREE node counts this page shows (app#209 item 1).
// The header and the Nodes ring print the COUNTED total; a network row prints the ACTIVE one, and
// the two were left side by side with one word between them.
//
// ⚠⚠ THE WORD IS "COUNTED" AND IT USED TO BE "BILLED" (Benoit, 2026-09-05). Devices bill on no
// plan — see `usageAxis.ts` — so every sentence here that said a node "bills" was a claim about a
// customer's money that nothing charges. Written the same way the storage
// note is: state the arithmetic, and where it does not close, name the reason (app#135 / DS-16).
//
// ⚠ BOTH DIRECTIONS ARE NAMED. Active above counted is the expected case — a node counts after
// 24 h cumulative online, so a device added this period is not counted yet. Counted above active
// is the other one: a node whose owner org has no network to show it under counts on the org axis
// and appears in no row here. A note that handled only the expected case would print the wrong reason
// for the other one, which is worse than printing none.
//
// ⚠⚠ EQUAL COUNTS DO NOT MEAN THE SAME NODES, so the agreeing branch states the arithmetic and
// claims nothing about the devices. Both differences are `max(0, ±diff)` of two TOTALS: one node
// waiting on 24 h and one node in no network of yours cancel out, and the counts agree while
// neither statement about a device is true. It said "every one has passed 24 h cumulative
// online" there, which is the one sentence that case makes false.
//
// ⚠ Nothing is rendered when the org has no nodes at all in either count. "0 active, 0 counted" is
// an arithmetic statement about nothing, under a list that already says there is no usage.
function NodeReconcileNote({ nodes }: Readonly<{ nodes: NodeReconciliation }>) {
  if (nodes.activeNodes === 0 && nodes.countedNodes === 0) return null
  const counts = `Nodes: ${nodes.activeNodes} active in your networks this period, ${nodes.countedNodes} counted`
  let verdict: string
  if (nodes.notYetCounted > 0) {
    verdict = ` — a node counts after 24 h cumulative online, so ${nodes.notYetCounted} ${nodes.notYetCounted === 1 ? 'is' : 'are'} not counted yet.`
  } else if (nodes.unattributedNodes > 0) {
    // A node counts for the org that OWNS it, whether or not that org has a network to show it in.
    verdict = ` — a node counts for the organization that owns it, so ${nodes.unattributedNodes} in no network of yours ${nodes.unattributedNodes === 1 ? 'counts' : 'count'} with no row above.`
  } else {
    verdict = ' — the Nodes ring above.'
  }
  return (
    <Tooltip
      arrow
      title="Active counts the nodes that reported anything this period. Counted is the nodes past 24 h cumulative online, including nodes deleted mid-period that had already passed it. A node is counted once, under one network, however many networks it is in. Devices are a plan limit, never a charge."
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {counts}
        {verdict}
      </Typography>
    </Tooltip>
  )
}

// Rings + the center projected-spend overlay, factored out to keep the main render readable.
//
// ⚠ Each ring is a FRACTION of its own allowance, so mixed units sit together honestly — a compute
// ring drawn from cents and a bandwidth ring drawn from bytes are both "how much of the pool".
// Nothing here formats an amount; the bars beside the rings carry the numbers, in their own units.
//
// ⚠⚠ THE CENTRE IS ONE NUMBER AND TAKES NO DENOMINATOR (app#202). It read
// "$20.00 / $100.00 projected" — the projected bill over `spend_cap_cents` — and that column bounds
// automatic credit refills, not the bill (nobgp-router#1195). It was the first figure on the page
// and the one an Owner quotes, built from two quantities that do not belong in one ratio. The
// props that fed it are gone with it, so a denominator cannot be reintroduced here by accident:
// see the deleted cap gauge in the parent for the whole argument.
function UsageRingsBlock({
  ringColor,
  axisFor,
  billCents,
  label,
}: Readonly<{
  ringColor: Record<string, string>
  axisFor: (name: string) => UsageAxis | undefined
  billCents: number
  label: string // 'projected' for the current month; a past month has nothing left to project
}>) {
  const rings = AXIS_ORDER.map(name => {
    const axis = axisFor(name)
    return { label: AXIS_LABEL[name], color: ringColor[name], used: axis?.used ?? 0, included: axis?.included ?? null }
  })
  return (
    <UsageRings rings={rings}>
      <Typography variant="h6" sx={{
        lineHeight: 1.1
      }}>
        {formatCents(billCents)}
      </Typography>
      <Typography variant="caption" sx={{
        color: 'text.secondary'
      }}>
        {label}
      </Typography>
    </UsageRings>
  )
}

// One per-axis bar: used vs included, green/amber/red, with paid-overage or free-cap treatment.
function UsageBar({
  axis,
  isFree,
  isPaid,
  color,
  resets,
  freshness,
}: Readonly<{
  axis: UsageAxis
  isFree: boolean
  isPaid: boolean
  color: string
  resets: string
  freshness: string
}>) {
  const under = [resetsOn(axis.axis) ? resets : '', freshness].filter(Boolean).join(' · ')
  const unlimited = axis.included == null
  // ⚠ `included` has THREE states — see `allowanceFraction`. The arm this replaced folded a REAL
  // allowance of zero into the unlimited branch, so an org with no pool and live usage got an
  // empty GREEN bar directly under the red "allowance used up" caption below.
  const fraction = allowanceFraction(axis.used, axis.included)
  const overage = (axis.overage ?? 0) > 0
  // ⚠⚠ COMPUTE ON A PLAN THAT IS NOT FREE HAS NO CEILING (app#327). Past the included amount it
  // continues from credit, so a bar that turns red and pins at 100% reads as a limit that is not
  // there. It stays one colour and the numbers say "used" and "included", never "of". A plan
  // that has not loaded takes this branch too: the bar must not claim a cap it cannot see.
  // ⚠ So this reads `!isFree`, not `isPaid`: "used · included" states no cap and names no
  // control. Only the "from credit" words below need a plan that IS paid.
  const includedOnly = axis.axis === 'compute' && !isFree && !unlimited
  const barColor: 'success' | 'warning' | 'error' =
    includedOnly || fraction < 0.8 ? 'success' : fraction < 0.95 ? 'warning' : 'error'

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'baseline'
        }}>
        <Stack direction="row" spacing={0.5} sx={{
          alignItems: 'center'
        }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
          <Typography variant="body2">
            {AXIS_LABEL[axis.axis]}
          </Typography>
          {axis.axis === 'nodes' && (
            <Tooltip
              arrow
              /* ⚠⚠ IT SAID "Bills after 24 h" AND "usage it already incurred". Neither is true:
                  devices bill on no plan — `0108` dropped the rate column and the router's own
                  overage sums bandwidth and compute only. What 24 h decides is when a device is
                  COUNTED, and the count is measured against a plan limit that is unlimited on
                  every plan we sell. */
              title="Counted after 24 h cumulative online — the first day is free. Includes devices deleted mid-period that had already passed 24 h. Devices are a plan limit, never a charge."
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  color: 'text.secondary'
                }}>
                <LuInfo size={13} />
              </Box>
            </Tooltip>
          )}
          {axis.axis === 'compute' && (
            /* ⚠ Money, like every compute figure on this panel (app#327). The size table in the
                Compute card below gives the real machine time per size. */
            <Tooltip
              arrow
              title="What your provisioned machines cost this month, billed per minute. One amount, shared by every machine size — a bigger machine uses it faster."
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  color: 'text.secondary'
                }}>
                <LuInfo size={13} />
              </Box>
            </Tooltip>
          )}
          {axis.axis === 'storage' && (
            <Tooltip arrow title="Billed at the period's peak storage level, so a cleaned-up spike still counts for the period it occurred.">
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  color: 'text.secondary'
                }}>
                <LuInfo size={13} />
              </Box>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {includedOnly
            ? `${axisValue(axis)} used · ${axisIncluded(axis)} included`
            : `${axisValue(axis)} / ${axisIncluded(axis)}`}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={unlimited ? 0 : Math.min(100, fraction * 100)}
        color={barColor}
        sx={{ my: 0.5 }}
      />
      {/* Under the bar, in the reader's order: when it comes back, and how old the number above is
          (app#170). Both are absent rather than guessed — a past period has no reset, and an axis
          with no measurement this period has no stamp.

          ⚠ The guard reads the JOINED line, never its inputs. `resets` is computed once for the
          whole row and is non-empty on the current period for EVERY axis, including the two that
          do not reset — so testing the inputs rendered an empty caption under storage and nodes
          whenever those had no stamp either. */}
      {under && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {under}
        </Typography>
      )}
      {overage &&
        (isFree ? (
          <Typography variant="caption" color="error">
            {/* ⚠ Compute gets its own sentence because its consequence differs in KIND: the other
                axes REFUSE something new, compute SUSPENDS work already running (router
                src/billing/alert_email.go, docs/PRICING.md §4.3). This is written as a remedy and
                not as a present-tense verdict — the app cannot read the router's enforcement
                switch, so it must not tell a customer their tasks have stopped. */}
            {axis.axis === 'compute'
              ? 'Compute allowance used up — upgrade to keep provisioned tasks running'
              : 'Limit reached — upgrade to add more'}
          </Typography>
        ) : axis.unit === 'cents' ? (
          /* ⚠ A cents-denominated axis is over by the SAME number it is charged: get_org_usage
             sets overage_cents = ceil(overage) for compute, so "+$12.34 over · $12.34" would read
             as two charges. Print the money once. */
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            {/* ⚠ On a paid plan this is the part paid from credit, not a breach (app#327).
                ⚠ "from credit" only on a plan that IS paid: `!isFree` is also true while the plan
                has not loaded, and then the line must not name a control the org may not have.
                The Compute card uses the same words for the same case. */}
            {formatCents(axis.overageCents)} {isPaid ? 'from credit' : 'past the included amount'}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            +{formatAxisAmount(axis.unit, axis.overage ?? 0)} over · {formatCents(axis.overageCents)}
          </Typography>
        ))}
    </Box>
  )
}
